/**
 * Conteneur pour le Widget Direct OpenSystem (Alliance Réseaux).
 *
 * Le noyau OpenSystem utilise document.getElementById pour trouver son conteneur,
 * ce qui est incompatible avec le Shadow DOM. On utilise un <iframe> qui possède
 * son propre document : getElementById fonctionne normalement à l'intérieur,
 * et l'iframe s'affiche dans le Shadow DOM parent (= dans la modal DetailView).
 *
 * Documentation : https://documentation.open-system.fr/api/widgetdirect/AllianceReseaux_OpenSystem_WidgetDirect.pdf
 * Exemple : https://trailencoterotie.fr/hebergements/#widget-details-offre-5801668
 */

import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

const NOYAU_URL =
  "https://gadget.open-system.fr/widgets-libs/rel/noyau-1.0.min.js";

@customElement("apidae-open-system-widget")
export class OpenSystemWidget extends LitElement {
  @property({ type: String })
  ui: string = "";

  @property({ type: String, attribute: "id-panier" })
  idPanier: string = "";

  @property({ type: Number, attribute: "id-integration" })
  idIntegration: number = 0;

  @property({ type: String })
  langue: string = "fr";

  @property({ type: String, attribute: "section-title" })
  sectionTitle: string = "Réservation en ligne";

  private setupKey = "";
  private resizeObserver: ResizeObserver | null = null;
  private heightPollTimer: ReturnType<typeof setTimeout> | null = null;

  static styles = css`
    :host {
      display: block;
    }
    iframe {
      width: 100%;
      border: none;
      display: block;
      min-height: 0;
      overflow: hidden;
      color-scheme: normal;
    }
  `;

  updated() {
    this.trySetupIframe();
  }

  disconnectedCallback() {
    this.teardown();
    super.disconnectedCallback();
  }

  private teardown() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.heightPollTimer != null) {
      clearTimeout(this.heightPollTimer);
      this.heightPollTimer = null;
    }
  }

  private trySetupIframe() {
    if (!this.ui || !this.idPanier || !this.idIntegration) return;

    const key = `${this.ui}|${this.idPanier}|${this.idIntegration}|${this.langue}`;
    if (key === this.setupKey) return;

    const iframe = this.renderRoot.querySelector(
      "iframe",
    ) as HTMLIFrameElement | null;
    if (!iframe) return;

    const doc = iframe.contentDocument;
    if (!doc) return;

    this.teardown();
    this.setupKey = key;

    const safeUi = this.escAttr(this.ui);
    const jsUi = this.escJs(this.ui);
    const jsPanier = this.escJs(this.idPanier);
    const jsLangue = this.escJs(this.langue);
    const jsIntegration = Number(this.idIntegration);

    doc.open();
    doc.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8">
<base href="https://gadget.open-system.fr/">
<style>html,body{margin:0;padding:0;overflow:visible;background:transparent;}</style>
</head><body>
<script>
(function(){
  var target="https://gadget.open-system.fr";
  function rewrite(url){
    if(!url||typeof url!=="string")return url;
    if(url.indexOf(target)===0)return url;
    var o=document.location.origin;
    var idx=url.indexOf("widgets/");
    if(idx===-1)return url;
    var ok=url.indexOf(o)===0||url.indexOf("http://localhost")===0||url.indexOf("https://localhost")===0;
    if(!ok)return url;
    return target+"/widgets/"+url.slice(idx+8);
  }
  function patch(parent){
    if(!parent||!parent.appendChild)return;
    var a=parent.appendChild.bind(parent),i=parent.insertBefore.bind(parent);
    parent.appendChild=function(n){
      if(n.tagName==="LINK"&&n.href)n.setAttribute("href",rewrite(n.getAttribute("href")||n.href));
      if(n.tagName==="SCRIPT"&&n.src)n.setAttribute("src",rewrite(n.getAttribute("src")||n.src));
      return a(n);
    };
    parent.insertBefore=function(n,r){
      if(n.tagName==="LINK"&&n.href)n.setAttribute("href",rewrite(n.getAttribute("href")||n.href));
      if(n.tagName==="SCRIPT"&&n.src)n.setAttribute("src",rewrite(n.getAttribute("src")||n.src));
      return i(n,r);
    };
  }
  patch(document.head);
  patch(document.body);
})();
</script>
<div id="widget-produit-${safeUi}" class="WidgetDirect"></div>
<script>
(function(){
  var s=document.createElement("script");
  s.src="${NOYAU_URL}";
  s.onload=function(){
    try{
      var w=AllianceReseaux.Widget.Instance("Produit",{
        idPanier:"${jsPanier}",
        idIntegration:${jsIntegration},
        langue:"${jsLangue}",
        ui:"${jsUi}"
      });
      if(w&&w.Initialise)w.Initialise();
    }catch(e){console.error("[OpenSystemWidget]",e);}
  };
  s.onerror=function(){console.error("[OpenSystemWidget] noyau failed to load");};
  document.head.appendChild(s);
})();
</script>
</body></html>`);
    doc.close();

    this.observeHeight(iframe);
  }

  private observeHeight(iframe: HTMLIFrameElement) {
    const sync = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const body = doc.body;
        const docEl = doc.documentElement;
        let h = Math.max(body?.scrollHeight ?? 0, docEl?.scrollHeight ?? 0);
        const widgetRoot = doc.querySelector(
          "[id^='widget-produit-']",
        ) as HTMLElement | null;
        if (widgetRoot?.scrollHeight) {
          h = Math.max(h, widgetRoot.scrollHeight);
        }
        if (h > 0) iframe.style.height = `${h}px`;
      } catch {
        /* cross-origin guard (ne devrait pas arriver avec doc.write) */
      }
    };

    sync();

    try {
      const doc = iframe.contentDocument;
      const body = doc?.body;
      if (body) {
        this.resizeObserver = new ResizeObserver(sync);
        this.resizeObserver.observe(body);
        const widgetRoot = doc.querySelector("[id^='widget-produit-']");
        if (widgetRoot) this.resizeObserver.observe(widgetRoot);
      }
    } catch {
      /* fallback polling ci-dessous */
    }

    let ticks = 0;
    const poll = () => {
      sync();
      ticks++;
      if (ticks <= 60) this.heightPollTimer = setTimeout(poll, 500);
    };
    this.heightPollTimer = window.setTimeout(poll, 500);
  }

  private escAttr(s: string): string {
    return s
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  private escJs(s: string): string {
    return s
      .replace(/\\/g, "\\\\")
      .replace(/"/g, '\\"')
      .replace(/'/g, "\\'")
      .replace(/\n/g, "\\n")
      .replace(/<\//g, "<\\/");
  }

  render() {
    if (!this.ui || !this.idPanier || !this.idIntegration) {
      return html``;
    }

    return html`
      <iframe title="${this.sectionTitle}" scrolling="no"></iframe>
    `;
  }
}
